import { useEffect, useState } from "react";
import type { MediaItem } from "@gooner/shared";
import { POS_REVIEW_TAGS, clipKey } from "@gooner/shared";
import { adminApi } from "../lib/api";
import { MediaSurface } from "../control/ui/MediaSurface";
import { cx } from "../control/ui/primitives";

/** The sidecar's predicted sex-position classes (see vision/app.py /health) — less
 *  `amazon`, which the head still predicts but is no longer a keyword (retired 2026-09-16),
 *  so it has nothing to review against. */
const MODEL_POSITIONS = [
  "blowjob",
  "cowgirl",
  "cunnilingus",
  "doggy",
  "facesitting",
  "footjob",
  "handjob",
  "missionary",
  "prone_bone",
  "reverse_cowgirl",
  "titfuck",
] as const;

/** Extra human-only categories the model doesn't predict yet — labelable here to build
 *  the dataset for a future retrain. The none-gate treats them as "not a sex position".
 *  `multifocus` = clips containing multiple things at once (handled properly later). */
const EXTRA_LABELS = ["dancing", "twerking", "tit_jiggle", "titty_drop", "female_masturbation", "striptease"] as const;

/** Everything selectable in the review tool: the 12 model classes + the extra categories. */
const POSITIONS = [...MODEL_POSITIONS, ...EXTRA_LABELS] as const;

/**
 * Internal tool (admin-only) for refining the position model: pick a label, scrub the
 * clips carrying its catalog slug (`position:doggy`, `act:handjob` — POS_REVIEW_TAGS,
 * since the pos: namespace became the position/act groups on 2026-09-12), and mark
 * each correct / reclassify / none. Each verdict fixes the live tag AND records a
 * human-verified label for the retraining dataset. Minimal by design — nobody but the
 * trainer sees it.
 */
const slugOf = (label: string) => POS_REVIEW_TAGS[label] ?? label;
export function PositionReview() {
  const [label, setLabel] = useState<string>("handjob");
  const [clips, setClips] = useState<MediaItem[] | null>(null);
  const [busy, setBusy] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setClips(null);
    adminApi
      .posReviewClips(label)
      .then((r) => !cancelled && setClips(r.items))
      .catch(() => !cancelled && setClips([]));
    return () => {
      cancelled = true;
    };
  }, [label]);

  async function apply(clip: MediaItem, chosen: string) {
    const id = clipKey(clip);
    setBusy((p) => new Set(p).add(id));
    try {
      await adminApi.setPosLabel(id, chosen);
      setClips((prev) => (prev ? prev.filter((c) => clipKey(c) !== id) : prev));
    } catch {
      setBusy((p) => {
        const n = new Set(p);
        n.delete(id);
        return n;
      });
    }
  }

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-lg font-semibold text-zinc-100">Position review</h1>
        <p className="text-xs text-zinc-500">
          Verify the position model's tags (<code>position:*</code> / <code>act:*</code>). Mark each clip correct, reclassify it, or
          “none”. Your verdict fixes the live tag and builds the retraining dataset
          (<code>GET /api/admin/pos/export</code>).
        </p>
      </header>

      <div className="flex flex-wrap gap-1.5">
        {POSITIONS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setLabel(p)}
            className={cx(
              "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
              p === label
                ? "bg-[var(--color-accent)] text-white"
                : "bg-[var(--color-panel-2)] text-zinc-300 hover:text-white",
            )}
          >
            {p}
          </button>
        ))}
        {/* The "none" queue = clips the model DIDN'T tag with a position. */}
        <button
          type="button"
          onClick={() => setLabel("none")}
          className={cx(
            "rounded-md border border-dashed px-2.5 py-1 text-xs font-medium transition-colors",
            label === "none"
              ? "border-transparent bg-[var(--color-accent)] text-white"
              : "border-[var(--color-edge)] text-zinc-400 hover:text-white",
          )}
        >
          none / unlabeled
        </button>
      </div>

      {clips === null ? (
        <p className="text-sm text-zinc-500">Loading…</p>
      ) : clips.length === 0 ? (
        <p className="py-12 text-center text-sm text-zinc-500">
          {label === "none"
            ? "No un-reviewed clips without a position."
            : `No un-reviewed clips tagged ${slugOf(label)}.`}
        </p>
      ) : (
        <>
          <p className="text-xs text-zinc-500">
            {clips.length} to review ·{" "}
            {label === "none" ? (
              <span className="text-zinc-300">no position tag</span>
            ) : (
              <span className="text-zinc-300">tagged {slugOf(label)}</span>
            )}
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {clips.map((clip) => {
              const id = clipKey(clip);
              const isBusy = busy.has(id);
              return (
                <div
                  key={id}
                  className={cx(
                    "space-y-2 rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel)] p-2",
                    isBusy && "pointer-events-none opacity-40",
                  )}
                >
                  <div className="relative aspect-[3/4] overflow-hidden rounded bg-black">
                    <MediaSurface kind={clip.kind} src={clip.url} poster={clip.posterUrl} fit="contain" loop />
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    {label === "none" ? (
                      // Reviewing the unlabeled queue: confirm "no position", or assign one.
                      <>
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() => void apply(clip, "none")}
                          className="rounded bg-emerald-600/80 px-2 py-1 text-[11px] font-semibold text-white hover:bg-emerald-600"
                        >
                          ✓ No position
                        </button>
                        <select
                          disabled={isBusy}
                          defaultValue=""
                          onChange={(e) => {
                            if (e.target.value) void apply(clip, e.target.value);
                          }}
                          className="rounded border border-[var(--color-edge)] bg-[var(--color-ink)] px-1 py-1 text-[11px] text-zinc-200"
                        >
                          <option value="" disabled>
                            Assign a position…
                          </option>
                          {POSITIONS.map((p) => (
                            <option key={p} value={p}>
                              {p}
                            </option>
                          ))}
                        </select>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() => void apply(clip, label)}
                          className="rounded bg-emerald-600/80 px-2 py-1 text-[11px] font-semibold text-white hover:bg-emerald-600"
                        >
                          ✓ Correct
                        </button>
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() => void apply(clip, "none")}
                          className="rounded bg-black/50 px-2 py-1 text-[11px] text-zinc-300 hover:bg-red-500/30"
                        >
                          None
                        </button>
                        <select
                          disabled={isBusy}
                          defaultValue=""
                          onChange={(e) => {
                            if (e.target.value) void apply(clip, e.target.value);
                          }}
                          className="rounded border border-[var(--color-edge)] bg-[var(--color-ink)] px-1 py-1 text-[11px] text-zinc-200"
                        >
                          <option value="" disabled>
                            Reclassify…
                          </option>
                          {POSITIONS.filter((p) => p !== label).map((p) => (
                            <option key={p} value={p}>
                              {p}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
